import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../../../apiClient";
import { listOfferings } from "../../../api/offeringsApi";
import {
  checkSpaceConflict,
  createEventHold,
  createVisitSeries,
  fetchArrivalGrid,
  getVenues,
} from "../../../api/bookingApi";
import { getActiveTenant } from "../../../helpers/tenantHelpers";
import "./schedule-composer.css";

/* SCHEDULE COMPOSER — scheduling happens inside the customer card.

   Same shape as the quote composer next door, and for the same reason: the
   record you are looking at IS the one being scheduled, so there is no step
   where a rep re-finds a customer they already had open.

   TWO SHAPES, BECAUSE THE BUSINESSES ARE GENUINELY DIFFERENT — not because of
   a style preference:

     a SPACE has to be free      (events)      -> booking/event-hold/
     a VISIT has to recur        (landscaping) -> booking/visit-series/
     a SLOT comes from FieldRoutes (pest)      -> untouched, see below

   Unlike the quote composer, this one does branch on industry, and that is
   correct: "is the Barn free from 2pm" and "mow every other Tuesday" are not
   two renderings of one form, they are different questions with different
   failure modes. What is NOT hardcoded is anything an OFFERING decides — the
   service list is the tenant's own catalogue.

   PEST IS DELIBERATELY NOT HANDLED HERE. t3's slots come from FieldRoutes and
   the existing flow owns them; a second scheduling path for pest would be two
   systems writing the same visit. The composer renders nothing for pest and
   says where to go instead.

   WHAT IS DEPLOYED, measured against prod on 2026-10-02 rather than read from
   a deploy report:
     POST booking/space-conflict/  live
     POST booking/event-hold/      live
     POST booking/visit-series/    live, but quote_id-driven ONLY
     GET  booking/venues/          404 — not deployed
   So the events half is live end to end today. The recurring half needs
   customer_id-driven visit-series, which is pushed but not merged, and it will
   refuse until that lands. The composer FEATURE-DETECTS and says so plainly
   rather than offering a control that cannot work. */

const EVENT_STATUSES = [
  // The stored value differs from the word a rep uses for exactly one of these,
  // so the label is the rep's word and `send` is the API's.
  { send: "inquiry", label: "Enquiry", hint: "Someone asked. The space is NOT held." },
  { send: "hold", label: "Hold", hint: "Tentative — the space is held." },
  { send: "booked", label: "Booked", hint: "Confirmed booking." },
];

const FREQUENCIES = [
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Every other week" },
  { value: "monthly", label: "Monthly" },
  { value: "bimonthly", label: "Every other month" },
  { value: "quarterly", label: "Quarterly" },
  { value: "semiannual", label: "Twice a year" },
  { value: "annual", label: "Yearly" },
];

/* ONE PLACE WHERE A DATE AND A TIME BECOME WHAT THE API RECEIVES.

   A naive "2026-11-14T14:00:00" is sent deliberately, with NO timezone suffix.
   The rep means two o'clock at the venue. Converting through the browser's
   clock (`new Date(...).toISOString()`) would send the right instant only when
   the rep's laptop happens to be in the tenant's timezone — an office manager
   working from another state would silently book a different hour, and nothing
   would error. A naive datetime lets the server apply the tenant's timezone,
   which is the only clock that means anything to a venue.

   This is the field most likely to be wrong, which is why it is one function:
   if the live check shows the server reads naive input as UTC, this is the
   only line to change. */
const naive = (date, time) => (date && time ? `${date}T${time}:00` : null);

const fmtTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 10);
  return d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
};

/* Which part of the OTHER booking we are running into. "Their teardown" reads
   very differently to a rep than "their event", so it is worth the words. */
const OVERLAP_WORDS = {
  event: "their event",
  setup: "their setup",
  teardown: "their teardown",
};

function ConflictList({ conflicts }) {
  if (!Array.isArray(conflicts) || !conflicts.length) return null;
  return (
    <ul className="sc-conflicts">
      {conflicts.map((c) => (
        <li key={c.appointment_id || `${c.start}-${c.end}`} className="sc-conflict">
          <span className="sc-conflict-who">{c.customer_name || "Another booking"}</span>
          <span className="sc-conflict-when">
            {fmtTime(c.start)} – {fmtTime(c.end)}
            {c.teardown_end ? ` (teardown to ${fmtTime(c.teardown_end)})` : ""}
          </span>
          <span className="sc-conflict-meta">
            {OVERLAP_WORDS[c.overlap] || c.overlap || "overlap"}
            {c.status ? ` · ${c.status}` : ""}
          </span>
        </li>
      ))}
    </ul>
  );
}

export default function ScheduleComposer({ customer, industry, onScheduled, onCancel }) {
  const tenant = getActiveTenant();
  const industryKey = String(industry || tenant?.industry || "general").toLowerCase();
  const customerId = customer?.customer_id || customer?.raw?.id || customer?.id || null;
  const isEvents = industryKey === "events";

  /* ----- shared ----- */
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // the ok:true response, read back
  const [error, setError] = useState("");

  /* ----- events half ----- */
  const [venues, setVenues] = useState(null); // null = loading, [] = none, array
  const [venuesFailed, setVenuesFailed] = useState(false);
  const [venueId, setVenueId] = useState("");
  const [spaceName, setSpaceName] = useState(""); // only when there is no picker
  const [date, setDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [setupTime, setSetupTime] = useState("");
  const [teardownTime, setTeardownTime] = useState("");
  const [status, setStatus] = useState("hold");
  const [eventType, setEventType] = useState("");
  const [guestCount, setGuestCount] = useState("");

  const [conflict, setConflict] = useState(null); // the space-conflict response
  const [checking, setChecking] = useState(false);
  const [overrideAck, setOverrideAck] = useState(false);

  /* ----- recurring half ----- */
  const [offerings, setOfferings] = useState(null);
  const [offeringId, setOfferingId] = useState("");
  const [frequency, setFrequency] = useState("biweekly");
  const [startDate, setStartDate] = useState("");
  const [grid, setGrid] = useState(undefined); // undefined=unasked, null=unknown, []=no grid
  const [arrivalWindow, setArrivalWindow] = useState("");
  const [freeWindowStart, setFreeWindowStart] = useState("");
  const [freeWindowEnd, setFreeWindowEnd] = useState("");
  const [crew, setCrew] = useState([]);
  const [crewId, setCrewId] = useState("");
  const [preview, setPreview] = useState(null); // dry-run response

  /* Load the space list (events) or the catalogue + crew (recurring). */
  useEffect(() => {
    let live = true;
    (async () => {
      if (isEvents) {
        const res = await getVenues();
        if (!live) return;
        if (res.ok && Array.isArray(res.venues)) {
          setVenues(res.venues);
          // One space is the common case (RT's Ranch has exactly one today).
          // Preselecting it removes a click that has no alternative, and it
          // means venue_id is always sent — which is what keeps the ambiguity
          // refusal from ever firing.
          if (res.venues.length === 1) setVenueId(String(res.venues[0].id));
        } else {
          setVenues(null);
          setVenuesFailed(true);
        }
        return;
      }
      const [offs, team] = await Promise.allSettled([
        listOfferings({ active: true }),
        // Verified live: /accounts/team/ answers. /accounts/users/ 404s — the
        // onboarding checklist found that the hard way.
        api.get("/accounts/team/"),
      ]);
      if (!live) return;
      const rows = (r) => {
        if (r.status !== "fulfilled") return [];
        const d = r.value?.data ?? r.value;
        return Array.isArray(d) ? d : Array.isArray(d?.results) ? d.results : [];
      };
      setOfferings(rows(offs));
      setCrew(rows(team));
    })();
    return () => {
      live = false;
    };
  }, [isEvents]);

  /* THE CONFLICT CHECK RUNS ON CHANGE, NOT ON SUBMIT.

     It writes nothing, so there is no reason to make a rep commit before
     telling them the room is taken. Debounced because a rep dragging through a
     time field would otherwise fire a request per keystroke. */
  const checkSeq = useRef(0);
  useEffect(() => {
    if (!isEvents) return undefined;
    const start = naive(date, startTime);
    const end = naive(date, endTime);
    if (!start || !end) {
      setConflict(null);
      return undefined;
    }
    const space = venueId ? { venue_id: Number(venueId) } : spaceName.trim() ? { space: spaceName.trim() } : null;
    if (!space) {
      setConflict(null);
      return undefined;
    }
    const seq = ++checkSeq.current;
    setChecking(true);
    const t = setTimeout(async () => {
      const res = await checkSpaceConflict({
        ...space,
        start,
        end,
        setup_start: naive(date, setupTime) || undefined,
        teardown_end: naive(date, teardownTime) || undefined,
        // Set when editing an existing booking so it cannot conflict with
        // itself. Nothing in the card edits yet; the field is wired so that
        // when it does, it is not forgotten.
        exclude_appointment_id: customer?.__editingAppointmentId || undefined,
      });
      // A slower earlier request must not overwrite a newer answer.
      if (seq !== checkSeq.current) return;
      setChecking(false);
      setConflict(res);
      // Any change invalidates a previous "yes, book it anyway".
      setOverrideAck(false);
    }, 300);
    return () => clearTimeout(t);
  }, [isEvents, venueId, spaceName, date, startTime, endTime, setupTime, teardownTime, customer]);

  /* Fetch the tenant's arrival grid when the recurring date changes.

     Per-DATE, not once: the grid is per-weekday with overrides, so a Saturday
     can offer different windows than a Tuesday. */
  useEffect(() => {
    if (isEvents || !customerId || !startDate) return undefined;
    let live = true;
    (async () => {
      const { windows } = await fetchArrivalGrid({
        customerId,
        offeringId: offeringId || undefined,
        startDate,
        frequency,
      });
      if (!live) return;
      setGrid(windows);
      // A window that is no longer offered on the new date must not stay
      // selected — it would be refused on submit for a reason the rep cannot
      // see, having picked it from a valid list a moment ago.
      if (Array.isArray(windows) && arrivalWindow && !windows.includes(arrivalWindow)) {
        setArrivalWindow("");
      }
    })();
    return () => {
      live = false;
    };
    // arrivalWindow is deliberately not a dependency: including it would
    // re-fetch the grid every time a rep picks a window.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEvents, customerId, startDate, offeringId, frequency]);

  /* Is the space in a state we are willing to book?

     THREE STATES, AND ONLY ONE OF THEM IS "GO":
       ok:true                  free
       space_double_booked      taken — bookable only with an explicit override
       anything else            NOT KNOWN — and not-known is never a go

     That last branch is the whole point. venue_unknown, window_invalid and a
     dropped connection all mean we did not establish the room is free, and a
     composer that treats "could not check" as "clear" books the Ruiz wedding
     on top of someone else. */
  const spaceState = useMemo(() => {
    if (!conflict) return "unchecked";
    if (conflict.ok) return "free";
    if (conflict.code === "space_double_booked") return "taken";
    return "unknown";
  }, [conflict]);

  const eventsReady =
    !!customerId &&
    !!date &&
    !!startTime &&
    !!endTime &&
    (!!venueId || !!spaceName.trim()) &&
    (spaceState === "free" || (spaceState === "taken" && overrideAck));

  /* The single reason the create button is disabled, in the order a rep fills
     the form, so a greyed-out "Hold the space" always says why — never a dead
     button with no explanation. "" means ready. */
  const eventsBlock = !customerId
    ? "Save the customer first."
    : !(venueId || spaceName.trim())
    ? "Choose a space."
    : !date
    ? "Pick a date."
    : !startTime || !endTime
    ? "Set a start and end time."
    : checking || spaceState === "unchecked"
    ? "Checking the space is free…"
    : spaceState === "unknown"
    ? conflict?.detail || "Couldn’t confirm the space is free — adjust the details and try again."
    : spaceState === "taken" && !overrideAck
    ? "That space is taken then — tick “Book it anyway” above to override."
    : "";

  const submitEvent = useCallback(async () => {
    setBusy(true);
    setError("");
    const space = venueId ? { venue_id: Number(venueId) } : { space: spaceName.trim() };
    const res = await createEventHold({
      customer_id: customerId,
      ...space,
      start: naive(date, startTime),
      end: naive(date, endTime),
      setup_start: naive(date, setupTime) || undefined,
      teardown_end: naive(date, teardownTime) || undefined,
      status,
      event_type: eventType.trim() || undefined,
      guest_count: guestCount === "" ? undefined : Number(guestCount),
      // Only ever sent after the rep has SEEN the conflicts and ticked the box.
      override_conflict: spaceState === "taken" && overrideAck ? true : undefined,
    });
    setBusy(false);
    if (!res.ok) {
      setError(res.detail || "The booking did not go through.");
      if (Array.isArray(res.conflicts) && res.conflicts.length) setConflict(res);
      return;
    }
    setResult(res);
    if (onScheduled) onScheduled(res);
  }, [
    customerId, venueId, spaceName, date, startTime, endTime, setupTime, teardownTime,
    status, eventType, guestCount, spaceState, overrideAck, onScheduled,
  ]);

  const runSeries = useCallback(
    async (dryRun) => {
      setBusy(true);
      setError("");
      const windowFields = arrivalWindow
        ? { arrival_window: arrivalWindow }
        : freeWindowStart && freeWindowEnd
          ? // The explicit escape hatch: skips the grid check. Used only where
            // there is no grid to pick from.
            { window_start: freeWindowStart, window_end: freeWindowEnd }
          : {};
      const res = await createVisitSeries({
        customer_id: customerId,
        offering_id: offeringId || undefined,
        frequency,
        start_date: startDate || undefined,
        crew_id: crewId || undefined,
        ...windowFields,
        dry_run: dryRun,
      });
      setBusy(false);
      if (!res.ok) {
        setError(res.detail || "The visits were not scheduled.");
        // The grid refusal carries the offered windows — adopt them so the
        // rep's next pick is from the real list rather than the same wrong box.
        if (Array.isArray(res.offered_windows)) setGrid(res.offered_windows);
        return;
      }
      if (dryRun) setPreview(res);
      else {
        setResult(res);
        if (onScheduled) onScheduled(res);
      }
    },
    [customerId, offeringId, frequency, startDate, crewId, arrivalWindow, freeWindowStart, freeWindowEnd, onScheduled],
  );

  /* ----- pest: not this composer's job ----- */
  if (industryKey === "pest_control") {
    return (
      <div className="sc-wrap">
        <p className="sc-note">
          Pest visits are scheduled from the dispatch board, which books the slot in FieldRoutes.
          Scheduling here as well would mean two systems writing the same visit.
        </p>
        <div className="sc-actions">
          <button type="button" className="cp-btn cp-btn--ghost" onClick={onCancel}>Close</button>
        </div>
      </div>
    );
  }

  if (!customerId) {
    return (
      <div className="sc-wrap">
        <p className="sc-note">Save this customer first — a booking has to belong to a record.</p>
        <div className="sc-actions">
          <button type="button" className="cp-btn cp-btn--ghost" onClick={onCancel}>Close</button>
        </div>
      </div>
    );
  }

  /* ----- booked ----- */
  if (result) {
    const visits = Array.isArray(result.visits) ? result.visits : null;
    return (
      <div className="sc-wrap">
        <div className="sc-done">
          <h4 className="sc-done-title">
            {visits
              ? result.created > 0
                ? `${result.created} visit${result.created === 1 ? "" : "s"} scheduled`
                : "Already scheduled"
              : /* The STORED status, read off the response — not the word that
                   was sent. "booked" is stored as "confirmed", and showing a
                   rep what the record actually says is the point. */
                `Booking saved — ${result.status || "saved"}`}
          </h4>
          {visits && result.skipped > 0 ? (
            <p className="sc-note">
              {result.skipped} date{result.skipped === 1 ? " was" : "s were"} already on the plan and
              {result.skipped === 1 ? " was" : " were"} left alone.
            </p>
          ) : null}
          {result.arrival_window ? (
            <p className="sc-note">Arrival window {result.arrival_window}.</p>
          ) : null}
          {result.service_plan_created ? (
            <p className="sc-note">A recurring plan was created for this service.</p>
          ) : null}
          {visits && visits.length ? (
            <ul className="sc-visits">
              {visits.slice(0, 6).map((v) => (
                <li key={v.appointment_id || v.start}>{fmtDate(v.start)} · {fmtTime(v.start)}</li>
              ))}
              {visits.length > 6 ? <li className="sc-muted">+{visits.length - 6} more</li> : null}
            </ul>
          ) : null}
        </div>
        <div className="sc-actions">
          <button type="button" className="cp-btn cp-btn--primary" onClick={onCancel}>Done</button>
        </div>
      </div>
    );
  }

  /* ----- events form ----- */
  if (isEvents) {
    return (
      <div className="sc-wrap">
        <div className="sc-grid">
          <label className="sc-field">
            <span className="sc-label">Space</span>
            {venues === null && !venuesFailed ? (
              <span className="sc-muted">Loading spaces…</span>
            ) : Array.isArray(venues) && venues.length ? (
              <select className="sc-input" value={venueId} onChange={(e) => setVenueId(e.target.value)}>
                <option value="">Choose a space…</option>
                {venues.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name}{v.capacity ? ` · up to ${v.capacity}` : ""}
                  </option>
                ))}
              </select>
            ) : (
              /* No picker. A typed name still works, but an ambiguous one is
                 refused rather than guessed, so say so before they type. */
              <>
                <input
                  className="sc-input"
                  value={spaceName}
                  onChange={(e) => setSpaceName(e.target.value)}
                  placeholder="Name of the space"
                />
                <span className="sc-hint">
                  Type the exact name. A name that matches two spaces is refused rather than guessed.
                </span>
              </>
            )}
          </label>

          <label className="sc-field">
            <span className="sc-label">Date</span>
            <input className="sc-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </label>

          <label className="sc-field">
            <span className="sc-label">Starts</span>
            <input className="sc-input" type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
          </label>

          <label className="sc-field">
            <span className="sc-label">Ends</span>
            <input className="sc-input" type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
          </label>

          <label className="sc-field">
            <span className="sc-label">Setup from</span>
            <input className="sc-input" type="time" value={setupTime} onChange={(e) => setSetupTime(e.target.value)} />
            <span className="sc-hint">Optional. The space is held from here.</span>
          </label>

          <label className="sc-field">
            <span className="sc-label">Teardown until</span>
            <input className="sc-input" type="time" value={teardownTime} onChange={(e) => setTeardownTime(e.target.value)} />
            <span className="sc-hint">Optional. The space is held until here.</span>
          </label>

          <label className="sc-field">
            <span className="sc-label">Event type</span>
            <input
              className="sc-input"
              value={eventType}
              onChange={(e) => setEventType(e.target.value)}
              placeholder="Wedding, birthday…"
              maxLength={100}
            />
          </label>

          <label className="sc-field">
            <span className="sc-label">Guests</span>
            <input
              className="sc-input"
              type="number"
              inputMode="numeric"
              min="0"
              value={guestCount}
              onChange={(e) => setGuestCount(e.target.value)}
            />
          </label>
        </div>

        <fieldset className="sc-statuses">
          <legend className="sc-label">Commitment</legend>
          {EVENT_STATUSES.map((s) => (
            <label key={s.send} className={`sc-status${status === s.send ? " is-on" : ""}`}>
              <input
                type="radio"
                name="sc-status"
                value={s.send}
                checked={status === s.send}
                onChange={() => setStatus(s.send)}
              />
              <span className="sc-status-label">{s.label}</span>
              <span className="sc-status-hint">{s.hint}</span>
            </label>
          ))}
        </fieldset>

        {/* The space's state, stated plainly. An enquiry does not hold the
            room, so the wording below must not imply it does. */}
        <div className={`sc-space-state is-${spaceState}`} role="status">
          {checking ? (
            <span>Checking the space…</span>
          ) : spaceState === "free" ? (
            <span>
              Free{conflict?.window_start ? ` from ${fmtTime(conflict.window_start)} to ${fmtTime(conflict.window_end)}` : ""}
              {status === "inquiry" ? " — an enquiry will not hold it." : ""}
            </span>
          ) : spaceState === "taken" ? (
            <>
              <span className="sc-state-head">{conflict.detail}</span>
              <ConflictList conflicts={conflict.conflicts} />
              <label className="sc-override">
                <input
                  type="checkbox"
                  checked={overrideAck}
                  onChange={(e) => setOverrideAck(e.target.checked)}
                />
                <span>Book it anyway — I have seen the conflict above.</span>
              </label>
            </>
          ) : spaceState === "unknown" ? (
            // Not free, not taken: unknown. Submit stays disabled.
            <span className="sc-state-head">{conflict.detail || "The space could not be checked."}</span>
          ) : (
            <span className="sc-muted">Pick a space, date and times to check availability.</span>
          )}
        </div>

        {error ? <p className="sc-error">{error}</p> : null}

        <div className="sc-actions sc-actions--sticky">
          {!eventsReady && !busy && eventsBlock ? (
            <span className="sc-block" role="status">{eventsBlock}</span>
          ) : null}
          <button type="button" className="cp-btn cp-btn--ghost" onClick={onCancel} disabled={busy}>Cancel</button>
          <button
            type="button"
            className="cp-btn cp-btn--primary"
            onClick={submitEvent}
            disabled={busy || !eventsReady}
          >
            {busy
              ? "Saving…"
              : status === "inquiry"
              ? "Log enquiry"
              : status === "booked"
              ? "Book the space"
              : "Hold the space"}
          </button>
        </div>
      </div>
    );
  }

  /* ----- recurring form (landscaping, general) ----- */
  return (
    <div className="sc-wrap">
      <div className="sc-grid">
        <label className="sc-field sc-field-wide">
          <span className="sc-label">Service</span>
          {offerings === null ? (
            <span className="sc-muted">Loading services…</span>
          ) : offerings.length ? (
            <select className="sc-input" value={offeringId} onChange={(e) => setOfferingId(e.target.value)}>
              <option value="">Use their existing plan</option>
              {offerings.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          ) : (
            <span className="sc-hint">
              No services in the catalogue yet — add one in Settings before scheduling a plan.
            </span>
          )}
        </label>

        <label className="sc-field">
          <span className="sc-label">How often</span>
          <select className="sc-input" value={frequency} onChange={(e) => setFrequency(e.target.value)}>
            {FREQUENCIES.map((f) => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
          </select>
        </label>

        <label className="sc-field">
          <span className="sc-label">First visit</span>
          <input className="sc-input" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </label>

        <label className="sc-field">
          <span className="sc-label">Crew</span>
          <select className="sc-input" value={crewId} onChange={(e) => setCrewId(e.target.value)}>
            <option value="">Unassigned</option>
            {crew.map((u) => (
              <option key={u.id} value={u.id}>
                {u.full_name || u.name || u.email || u.username}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* ARRIVAL WINDOW — buttons when the tenant has a grid, a text pair when
          they do not. Buttons mean a rep cannot pick a window the office
          cannot hit, so the server's refusal becomes a backstop instead of an
          error a human has to read.

          Three distinct states, and conflating any two of them breaks
          somebody: a grid (buttons), no grid (free text — the tenant has not
          set business hours, and default-open means scheduling must still
          work), and unknown (free text too, because the deployed build does
          not support the grid yet). */}
      <div className="sc-windows">
        <span className="sc-label">Arrival window</span>
        {Array.isArray(grid) && grid.length ? (
          <div className="sc-window-row">
            {grid.map((w) => (
              <button
                key={w}
                type="button"
                className={`sc-window${arrivalWindow === w ? " is-on" : ""}`}
                onClick={() => setArrivalWindow(arrivalWindow === w ? "" : w)}
              >
                {w}
              </button>
            ))}
          </div>
        ) : (
          <>
            <div className="sc-window-free">
              <input
                className="sc-input"
                type="time"
                value={freeWindowStart}
                onChange={(e) => setFreeWindowStart(e.target.value)}
                aria-label="Arrival window start"
              />
              <span className="sc-muted">to</span>
              <input
                className="sc-input"
                type="time"
                value={freeWindowEnd}
                onChange={(e) => setFreeWindowEnd(e.target.value)}
                aria-label="Arrival window end"
              />
            </div>
            <span className="sc-hint">
              {grid === null
                ? "This account has no arrival-window grid set up, so any window is accepted."
                : "Leave blank for a one-hour visit at 9am."}
            </span>
          </>
        )}
      </div>

      {preview ? (
        <div className="sc-preview">
          <h5 className="sc-preview-title">
            {preview.created === 0 && preview.skipped > 0
              ? `All ${preview.skipped} dates are already on the plan`
              : `${(preview.visits || []).length} visits, every ${preview.interval_days} days`}
          </h5>
          <ul className="sc-visits">
            {(preview.visits || []).slice(0, 8).map((v) => (
              <li key={v.start}>{fmtDate(v.start)} · {fmtTime(v.start)}</li>
            ))}
            {(preview.visits || []).length > 8 ? (
              <li className="sc-muted">+{(preview.visits || []).length - 8} more</li>
            ) : null}
          </ul>
        </div>
      ) : null}

      {error ? <p className="sc-error">{error}</p> : null}

      <div className="sc-actions sc-actions--sticky">
        <button type="button" className="cp-btn cp-btn--ghost" onClick={onCancel} disabled={busy}>Cancel</button>
        <button type="button" className="cp-btn cp-btn--ghost" onClick={() => runSeries(true)} disabled={busy || !startDate}>
          {busy ? "Working…" : "Preview"}
        </button>
        <button
          type="button"
          className="cp-btn cp-btn--primary"
          onClick={() => runSeries(false)}
          /* A rep sees the dates before a year of visits is written. */
          disabled={busy || !preview}
        >
          Schedule the visits
        </button>
      </div>
    </div>
  );
}
